import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  cancelCourierOrderApi,
  createCourierOrderApi,
  generateCourierInvoiceApi,
  generateCourierLabelApi,
  getAvailableCouriersApi,
  shipNowCourierApi,
} from "../../api/ecommerce/CourierApi";

const initialState = {
  availableCouriers: {},
  courierOrder: {},
  shipNowResponse: {},
  cancelResponse: {},
  labelResponse: {},
  invoiceResponse: {},
  loading: false,
  creating: false,
  shipping: false,
  cancelling: false,
  labelLoading: false,
  invoiceLoading: false,
};

const getApiPayload = (payload) => payload?.response ?? payload ?? {};

export const getAvailableCouriers = createAsyncThunk(
  "courier/getAvailableCouriers",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getAvailableCouriersApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  },
);

export const createCourierOrder = createAsyncThunk(
  "courier/createCourierOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCourierOrderApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  },
);

export const shipNowCourier = createAsyncThunk(
  "courier/shipNowCourier",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await shipNowCourierApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  },
);

export const cancelCourierOrder = createAsyncThunk(
  "courier/cancelCourierOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cancelCourierOrderApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  },
);

export const generateCourierLabel = createAsyncThunk(
  "courier/generateCourierLabel",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await generateCourierLabelApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  },
);

export const generateCourierInvoice = createAsyncThunk(
  "courier/generateCourierInvoice",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await generateCourierInvoiceApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.message,
      });
    }
  },
);

const courierSlice = createSlice({
  name: "courier",
  initialState,
  reducers: {
    clearCourierState: () => initialState,
    clearCourierOrder: (state) => {
      state.courierOrder = {};
      state.shipNowResponse = {};
      state.cancelResponse = {};
    },
    clearCourierDocuments: (state) => {
      state.labelResponse = {};
      state.invoiceResponse = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAvailableCouriers.pending, (state) => {
      state.loading = true;
      state.availableCouriers = {};
    });
    builder.addCase(getAvailableCouriers.fulfilled, (state, action) => {
      state.availableCouriers = getApiPayload(action?.payload);
      state.loading = false;
    });
    builder.addCase(getAvailableCouriers.rejected, (state) => {
      state.availableCouriers = {};
      state.loading = false;
    });

    builder.addCase(createCourierOrder.pending, (state) => {
      state.creating = true;
      state.courierOrder = {};
    });
    builder.addCase(createCourierOrder.fulfilled, (state, action) => {
      state.courierOrder = getApiPayload(action?.payload);
      state.creating = false;
    });
    builder.addCase(createCourierOrder.rejected, (state) => {
      state.courierOrder = {};
      state.creating = false;
    });

    builder.addCase(shipNowCourier.pending, (state) => {
      state.shipping = true;
      state.shipNowResponse = {};
    });
    builder.addCase(shipNowCourier.fulfilled, (state, action) => {
      state.shipNowResponse = getApiPayload(action?.payload);
      state.shipping = false;
    });
    builder.addCase(shipNowCourier.rejected, (state) => {
      state.shipNowResponse = {};
      state.shipping = false;
    });

    builder.addCase(cancelCourierOrder.pending, (state) => {
      state.cancelling = true;
      state.cancelResponse = {};
    });
    builder.addCase(cancelCourierOrder.fulfilled, (state, action) => {
      state.cancelResponse = getApiPayload(action?.payload);
      state.cancelling = false;
    });
    builder.addCase(cancelCourierOrder.rejected, (state) => {
      state.cancelResponse = {};
      state.cancelling = false;
    });

    builder.addCase(generateCourierLabel.pending, (state) => {
      state.labelLoading = true;
      state.labelResponse = {};
    });
    builder.addCase(generateCourierLabel.fulfilled, (state, action) => {
      state.labelResponse = getApiPayload(action?.payload);
      state.labelLoading = false;
    });
    builder.addCase(generateCourierLabel.rejected, (state) => {
      state.labelResponse = {};
      state.labelLoading = false;
    });

    builder.addCase(generateCourierInvoice.pending, (state) => {
      state.invoiceLoading = true;
      state.invoiceResponse = {};
    });
    builder.addCase(generateCourierInvoice.fulfilled, (state, action) => {
      state.invoiceResponse = getApiPayload(action?.payload);
      state.invoiceLoading = false;
    });
    builder.addCase(generateCourierInvoice.rejected, (state) => {
      state.invoiceResponse = {};
      state.invoiceLoading = false;
    });
  },
});

export const {
  clearCourierDocuments,
  clearCourierOrder,
  clearCourierState,
} = courierSlice.actions;

export default courierSlice.reducer;
